import { Box, Fab, Menu, MenuItem, Typography } from '@mui/material';
import { useState } from 'react';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';

interface SupportButtonProps {
  buttonSize?: 'small' | 'large';
  QALink?: string;
  supportEmail?: string;
  supportTelephone?: string;
}

/**
 * Support button with proper menu positioning
 *
 * This is a local implementation to fix positioning issues with AgronodSupport from @agronod/mui-components.
 * The menu is positioned above the button using correct MUI Menu anchorOrigin and transformOrigin.
 */
export default function SupportButton({
  buttonSize = 'large',
  QALink = 'https://www.agronod.com/fragor',
  supportEmail = 'support@agronod.com',
}: SupportButtonProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleContactUs = () => {
    handleClose();
    // Open mailto link with pre-filled email
    const subject = encodeURIComponent('Kontakt från Agrokoll');
    const body = encodeURIComponent(`Hej Agronod-support,\n\nJag skulle vilja ha hjälp med:\n\n[Beskriv ditt ärende här]\n\nMed vänliga hälsningar`);
    window.location.href = `mailto:${supportEmail}?subject=${subject}&body=${body}`;
  };

  const handleFAQ = () => {
    handleClose();
    window.open(QALink, '_blank');
  };

  return (
    <Box sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
      <Fab
        id="supportButton"
        aria-controls={open ? 'support-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        disableRipple={true}
        onClick={handleClick}
        variant="extended"
        sx={(theme) => ({
          height: buttonSize === 'large' ? '56px' : '32px',
          border: '1px solid',
          borderColor: 'hsla(45, 3%, 27%, 0.16)',
          backgroundColor: '#FFFFFF',
          color: theme.palette.text.primary,
          gap: buttonSize === 'large' ? 1 : 0.5,
          boxShadow: 'none',
          textTransform: 'none',
          fontSize: buttonSize === 'large' ? '16px' : '14px',
          fontWeight: 500,
          fontFamily: 'Inter, sans-serif',
          paddingLeft: buttonSize === 'large' ? '20px' : '12px',
          paddingRight: buttonSize === 'large' ? '20px' : '12px',
          '&:active': {
            boxShadow: 'none',
          },
          '&:hover': {
            borderColor: 'hsla(45, 3%, 27%, 0.24)',
            backgroundColor: '#FFFFFF',
          },
          '&:focus-visible': {
            boxShadow: '0px 0px 0px 4.5px #FFFFFF',
            borderColor: '#FFFFFF',
            outline: '2px solid hsla(45, 3%, 27%, 1)',
          },
        })}
      >
        <QuestionMarkIcon
          fontSize={buttonSize === 'large' ? 'medium' : 'small'}
          sx={{
            color: 'hsla(45, 3%, 27%, 0.69)',
          }}
        />
        Hjälp
      </Fab>

      <Menu
        id="support-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock={true}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              boxShadow: '0px 4px 16px 0px hsla(45, 3%, 27%, 0.08)',
              mt: -1,
              minWidth: 180,
              border: '1px solid',
              borderColor: 'hsla(45, 3%, 27%, 0.16)',
              borderRadius: '8px',
              backgroundColor: '#FFFFFF',
            },
          },

          list: {
            'aria-labelledby': 'supportButton',
            sx: {
              py: 0,
            },
          }
        }}>
        <MenuItem
          onClick={handleContactUs}
          sx={{
            py: 1.5,
            px: 2,
            minHeight: 48,
            '&:hover': {
              backgroundColor: 'hsla(45, 3%, 27%, 0.04)',
            },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              fontFamily: 'Inter, sans-serif',
              color: 'text.primary',
            }}
          >
            Kontakta oss
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={handleFAQ}
          sx={{
            py: 1.5,
            px: 2,
            minHeight: 48,
            '&:hover': {
              backgroundColor: 'hsla(45, 3%, 27%, 0.04)',
            },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              fontFamily: 'Inter, sans-serif',
              color: 'text.primary',
            }}
          >
            Vanliga frågor
          </Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
}
